import { useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PatientDocuments from '@/components/PatientDocuments/PatientDocuments';
import PendingAttachments, { StagedAttachment } from './PendingAttachments';

interface AttachmentsSectionProps {
  patientId: string;
  prescriptionId?: string;
  onPendingChange?: (items: StagedAttachment[]) => void;
}

// A compact trigger (icon + count badge), not a section in the scrolling
// form flow — most prescriptions never touch attachments, so this shouldn't
// cost vertical space by default. Opens the same upload/list UI in a dialog.
// A saved prescription (real prescriptionId) uploads immediately via
// PatientDocuments. A prescription still being created has no id yet, so
// files are staged locally (PendingAttachments) and uploaded once the parent
// form saves — see PrescriptionForm.tsx's handleSaved.
export default function AttachmentsSection({ patientId, prescriptionId, onPendingChange }: AttachmentsSectionProps) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);

  const handlePendingChange = (items: StagedAttachment[]) => {
    onPendingChange?.(items);
    setCount(items.length);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-info hover:border-info/40 hover:bg-info/5 transition-colors"
      >
        <Paperclip className="w-3.5 h-3.5" />
        Attachments
        {count > 0 && (
          <span className="flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-info text-white text-[10px] font-semibold tabular-nums">
            {count}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4" /> Attachments
            </DialogTitle>
          </DialogHeader>
          {prescriptionId ? (
            <PatientDocuments patientId={patientId} link={{ prescriptionId }} onCountChange={setCount} />
          ) : (
            <PendingAttachments onChange={handlePendingChange} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
